import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Grid, TextField, Select, MenuItem, FormControl,
  InputLabel, Button, Typography, IconButton, Divider,
  Table, TableHead, TableBody, TableRow, TableCell,
  Alert, Autocomplete, InputAdornment, Chip
} from '@mui/material';
import { Add, Delete, Close } from '@mui/icons-material';
import { billingAPI, patientAPI } from '../services/api';

const ITEM_CATEGORIES = ['Consultation', 'Procedure', 'Medicine', 'Session', 'Other'];
const PAYMENT_MODES = ['Cash', 'Card', 'UPI', 'Credit (Hold)', 'Insurance', 'Cheque', 'Other'];
const INVOICE_TYPES = ['Visitation', 'Treatment', 'Sale', 'Other'];

const emptyItem = () => ({ description: '', category: 'Consultation', quantity: 1, unitPrice: '', amount: 0 });
const emptyPayment = () => ({ mode: 'Cash', amount: '', reference: '' });

function InvoiceForm({ open, onClose, onSaved, editInvoice, prePopulate }) {
  const defaultForm = {
    patientId: '',
    patientName: '',
    patientPhone: '',
    type: 'Other',
    visitationId: '',
    treatmentId: '',
    saleId: '',
    items: [emptyItem()],
    subtotal: 0,
    discount: 0,
    discountPercentage: 0,
    tax: 0,
    taxPercentage: 0,
    totalAmount: 0,
    invoiceDate: new Date().toISOString().slice(0, 10),
    dueDate: '',
    notes: '',
    payments: [emptyPayment()]
  };

  const [form, setForm] = useState(defaultForm);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientInput, setPatientInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      if (editInvoice) {
        setForm({
          ...editInvoice,
          payments: editInvoice.payments?.length ? editInvoice.payments : [emptyPayment()]
        });
        setSelectedPatient(editInvoice.patient || null);
        setPatientInput(editInvoice.patientName || '');
      } else if (prePopulate) {
        // Pre-fill from a session or visitation record
        const prefilled = {
          ...defaultForm,
          patientId: prePopulate.patientId || '',
          patientName: prePopulate.patientName || '',
          patientPhone: prePopulate.patientPhone || '',
          type: prePopulate.type || 'Other',
          visitationId: prePopulate.source === 'visitation' ? prePopulate.visitationId : '',
          treatmentId: prePopulate.source === 'session' ? prePopulate.treatmentId : '',
          items: prePopulate.items?.length
            ? prePopulate.items.map(it => ({ ...it }))
            : [emptyItem()],
          invoiceDate: prePopulate.invoiceDate || new Date().toISOString().slice(0, 10),
          notes: prePopulate.notes || ''
        };
        // Recalculate totals from pre-filled items
        const subtotal = prefilled.items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
        prefilled.subtotal = subtotal;
        prefilled.totalAmount = subtotal;
        setForm(prefilled);
        setSelectedPatient(prePopulate.patient || null);
        setPatientInput(prePopulate.patientName || '');
      } else {
        setForm(defaultForm);
        setSelectedPatient(null);
        setPatientInput('');
      }
      setError('');
    }
  }, [open, editInvoice, prePopulate]); // eslint-disable-line

  const loadPatients = useCallback(async (search) => {
    if (!search || search.length < 2) { setPatients([]); return; }
    try {
      const res = await patientAPI.getAll({ search, limit: 20 });
      setPatients(res.data || []);
    } catch (_) { setPatients([]); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => loadPatients(patientInput), 300);
    return () => clearTimeout(t);
  }, [patientInput, loadPatients]);

  // ── Derived calculations ────────────────────────────────────────────────────
  const recalcTotals = (items, discountPct, taxPct) => {
    const subtotal = items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
    const discount = parseFloat(((subtotal * discountPct) / 100).toFixed(2));
    const tax = parseFloat((((subtotal - discount) * taxPct) / 100).toFixed(2));
    const total = parseFloat((subtotal - discount + tax).toFixed(2));
    return { subtotal, discount, tax, totalAmount: total };
  };

  const updateItem = (idx, field, value) => {
    setForm(prev => {
      const items = prev.items.map((it, i) => {
        if (i !== idx) return it;
        const updated = { ...it, [field]: value };
        if (field === 'quantity' || field === 'unitPrice') {
          const qty = parseFloat(field === 'quantity' ? value : it.quantity) || 0;
          const price = parseFloat(field === 'unitPrice' ? value : it.unitPrice) || 0;
          updated.amount = parseFloat((qty * price).toFixed(2));
        }
        return updated;
      });
      const totals = recalcTotals(items, prev.discountPercentage, prev.taxPercentage);
      return { ...prev, items, ...totals };
    });
  };

  const addItem = () => setForm(prev => ({ ...prev, items: [...prev.items, emptyItem()] }));

  const removeItem = (idx) => {
    setForm(prev => {
      const items = prev.items.filter((_, i) => i !== idx);
      const totals = recalcTotals(items, prev.discountPercentage, prev.taxPercentage);
      return { ...prev, items, ...totals };
    });
  };

  const updateDiscount = (val) => {
    const pct = parseFloat(val) || 0;
    setForm(prev => {
      const totals = recalcTotals(prev.items, pct, prev.taxPercentage);
      return { ...prev, discountPercentage: pct, ...totals };
    });
  };

  const updateTax = (val) => {
    const pct = parseFloat(val) || 0;
    setForm(prev => {
      const totals = recalcTotals(prev.items, prev.discountPercentage, pct);
      return { ...prev, taxPercentage: pct, ...totals };
    });
  };

  // ── Payment rows ────────────────────────────────────────────────────────────
  const addPaymentRow = () => setForm(prev => ({ ...prev, payments: [...prev.payments, emptyPayment()] }));

  const removePaymentRow = (idx) => {
    setForm(prev => ({ ...prev, payments: prev.payments.filter((_, i) => i !== idx) }));
  };

  const updatePayment = (idx, field, value) => {
    setForm(prev => ({
      ...prev,
      payments: prev.payments.map((p, i) => i === idx ? { ...p, [field]: value } : p)
    }));
  };

  const totalPaymentsEntered = form.payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.patientName.trim()) { setError('Patient name is required'); return; }
    if (!form.items.length || form.items.every(it => !it.description)) {
      setError('Add at least one invoice item'); return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        patientId: selectedPatient?.id || form.patientId || null,
        payments: form.payments.filter(p => parseFloat(p.amount) > 0)
      };
      if (editInvoice) {
        await billingAPI.updateInvoice(editInvoice.id, payload);
      } else {
        await billingAPI.createInvoice(payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const fmt = (n) => `₹${(parseFloat(n) || 0).toFixed(2)}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          {editInvoice ? `Edit Invoice — ${editInvoice.invoiceNumber}` : 'New Invoice'}
        </Typography>
        <IconButton onClick={onClose}><Close /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ── Section: Patient & Basic Info ────────────────────────────────── */}
        <Typography variant="subtitle2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
          Patient & Invoice Info
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={5}>
            <Autocomplete
              options={patients}
              getOptionLabel={(p) => p.firstName ? `${p.firstName} ${p.lastName} (${p.phone || ''})` : ''}
              inputValue={patientInput}
              value={selectedPatient}
              onInputChange={(_, val) => {
                setPatientInput(val);
                setForm(prev => ({ ...prev, patientName: val }));
              }}
              onChange={(_, patient) => {
                setSelectedPatient(patient);
                if (patient) {
                  setForm(prev => ({
                    ...prev,
                    patientId: patient.id,
                    patientName: `${patient.firstName} ${patient.lastName}`,
                    patientPhone: patient.phone || ''
                  }));
                  setPatientInput(`${patient.firstName} ${patient.lastName}`);
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Search Patient" size="small" fullWidth
                  placeholder="Type to search or enter name" />
              )}
              freeSolo
              filterOptions={(x) => x}
              noOptionsText="No patients found"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField size="small" fullWidth label="Patient Name *"
              value={form.patientName}
              onChange={e => setForm(prev => ({ ...prev, patientName: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField size="small" fullWidth label="Phone"
              value={form.patientPhone}
              onChange={e => setForm(prev => ({ ...prev, patientPhone: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select value={form.type} label="Type"
                onChange={e => setForm(prev => ({ ...prev, type: e.target.value }))}>
                {INVOICE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField size="small" fullWidth label="Invoice Date" type="date"
              value={form.invoiceDate}
              onChange={e => setForm(prev => ({ ...prev, invoiceDate: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField size="small" fullWidth label="Due Date" type="date"
              value={form.dueDate}
              onChange={e => setForm(prev => ({ ...prev, dueDate: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField size="small" fullWidth label="Reference ID (optional)"
              placeholder="Visit / Treatment / Sale ID"
              value={form.visitationId || form.treatmentId || form.saleId || ''}
              onChange={e => {
                const v = e.target.value;
                if (form.type === 'Visitation') setForm(prev => ({ ...prev, visitationId: v }));
                else if (form.type === 'Treatment') setForm(prev => ({ ...prev, treatmentId: v }));
                else if (form.type === 'Sale') setForm(prev => ({ ...prev, saleId: v }));
              }} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* ── Section: Line Items ────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" color="primary" fontWeight={600}>Line Items</Typography>
          <Button size="small" startIcon={<Add />} onClick={addItem}>Add Item</Button>
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ minWidth: 200 }}>Description</TableCell>
                <TableCell sx={{ minWidth: 130 }}>Category</TableCell>
                <TableCell align="right" sx={{ minWidth: 80 }}>Qty</TableCell>
                <TableCell align="right" sx={{ minWidth: 110 }}>Unit Price (₹)</TableCell>
                <TableCell align="right" sx={{ minWidth: 100 }}>Amount (₹)</TableCell>
                <TableCell sx={{ width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {form.items.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <TextField size="small" fullWidth value={item.description} variant="standard"
                      placeholder="e.g. Consultation Fee"
                      onChange={e => updateItem(idx, 'description', e.target.value)} />
                  </TableCell>
                  <TableCell>
                    <Select size="small" value={item.category} variant="standard" fullWidth
                      onChange={e => updateItem(idx, 'category', e.target.value)}>
                      {ITEM_CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <TextField size="small" type="number" value={item.quantity} variant="standard"
                      inputProps={{ min: 1, style: { textAlign: 'right' } }}
                      onChange={e => updateItem(idx, 'quantity', e.target.value)} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" type="number" value={item.unitPrice} variant="standard"
                      inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                      onChange={e => updateItem(idx, 'unitPrice', e.target.value)} />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={500}>
                      {(parseFloat(item.amount) || 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => removeItem(idx)}
                      disabled={form.items.length === 1}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {/* ── Pricing summary ─────────────────────────────────────────────── */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ width: { xs: '100%', sm: 340 } }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={6}><Typography variant="body2" color="text.secondary">Subtotal</Typography></Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="body2">{fmt(form.subtotal)}</Typography>
              </Grid>

              <Grid item xs={4}><Typography variant="body2" color="text.secondary">Discount %</Typography></Grid>
              <Grid item xs={3}>
                <TextField size="small" type="number" value={form.discountPercentage}
                  inputProps={{ min: 0, max: 100, step: '0.1' }}
                  onChange={e => updateDiscount(e.target.value)}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
              </Grid>
              <Grid item xs={5} sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="error.main">- {fmt(form.discount)}</Typography>
              </Grid>

              <Grid item xs={4}><Typography variant="body2" color="text.secondary">Tax %</Typography></Grid>
              <Grid item xs={3}>
                <TextField size="small" type="number" value={form.taxPercentage}
                  inputProps={{ min: 0, max: 100, step: '0.1' }}
                  onChange={e => updateTax(e.target.value)}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
              </Grid>
              <Grid item xs={5} sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="success.main">+ {fmt(form.tax)}</Typography>
              </Grid>

              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1" fontWeight={700}>Total</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle1" fontWeight={700} color="primary">
                  {fmt(form.totalAmount)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* ── Section: Payments ──────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" color="primary" fontWeight={600}>
            Payments (split across multiple modes)
          </Typography>
          <Button size="small" startIcon={<Add />} onClick={addPaymentRow}>Add Mode</Button>
        </Box>

        {form.payments.map((pay, idx) => (
          <Grid container spacing={2} key={idx} sx={{ mb: 1 }} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Mode</InputLabel>
                <Select value={pay.mode} label="Mode"
                  onChange={e => updatePayment(idx, 'mode', e.target.value)}>
                  {PAYMENT_MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth size="small" label="Amount (₹)" type="number"
                value={pay.amount}
                inputProps={{ min: 0, step: '0.01' }}
                onChange={e => updatePayment(idx, 'amount', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Reference / Transaction ID"
                value={pay.reference}
                onChange={e => updatePayment(idx, 'reference', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton size="small" onClick={() => removePaymentRow(idx)}
                disabled={form.payments.length === 1}>
                <Delete fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        ))}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 2, flexWrap: 'wrap' }}>
          <Chip label={`Paid: ${fmt(totalPaymentsEntered)}`} color="success" size="small" />
          <Chip
            label={`Due: ${fmt(Math.max(0, form.totalAmount - totalPaymentsEntered))}`}
            color={totalPaymentsEntered >= form.totalAmount ? 'success' : 'warning'}
            size="small" />
        </Box>

        {/* ── Notes ───────────────────────────────────────────────────────── */}
        <Divider sx={{ my: 2 }} />
        <TextField fullWidth multiline rows={2} label="Notes (optional)"
          value={form.notes}
          onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))} />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : editInvoice ? 'Update Invoice' : 'Create Invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default InvoiceForm;
